/**
 * Auth state cleanup — utility condivisa tra i 3 path di "uscita" dalla sessione:
 *   1. `AppStateHolder.signOut()` — click manuale dal user-menu.
 *   2. `AuthSessionService.clearSessionAndGoToLogin()` — recovery dopo 401.
 *   3. `main.ts redirectToLogin()` — prewarm fail / sessione zombie pre-bootstrap.
 *
 * Devono produrre lo stesso stato finale, altrimenti il rilogin riparte con
 * residui (sessionStorage stantio, cookie cross-subdomain non cancellato).
 *
 * Estratto in pure function (no Angular DI) per essere usabile anche da
 * `main.ts` prima del bootstrap di AppModule.
 */

export const AUTH_COOKIE_NAMES = [
  'authType',
  'authToken',
  'authTypeDomain',
  'ImpersonateState',
  'ImpersonationSessionId',
] as const;

/**
 * Chiavi sessionStorage che gating-ano il bootstrap del cookie `.AspNet.Cookies`
 * server-side ([app.component.ts:181](app.component.ts:181)).
 *
 * Se non vengono rimosse al logout, il rilogin trova `cookieBootstrapped=true`,
 * salta `bootstrapBackendCookie()` e le satellite (calendar in iframe, ecc.)
 * ricevono 401 sul cross-site call al backend.
 */
export const AUTH_SESSION_STORAGE_KEYS = [
  'cookieBootstrapped',
  'cookieBootstrapAttempt',
] as const;

/**
 * Cancella i cookie di auth scritti da `AppStateHolder.setCookie` su `.neworg.net`
 * (o `localhost` in dev).
 *
 * `Domain` DEVE matchare la scrittura: senza specificare `Domain` il browser
 * crea un nuovo cookie host-scoped (es. solo `amsuite.neworg.net`) e quello
 * cross-subdomain originale resta intatto e visibile dalle satellite.
 *
 * In codice Angular preferire `AppStateHolder.removeCookie` (stessa logica via
 * `ngx-cookie`). Questo helper esiste per `main.ts` che gira pre-bootstrap.
 */
export function clearAuthCookies(): void {
  const domain = location.hostname.includes('localhost') ? 'localhost' : '.neworg.net';
  const expiredAt = 'Thu, 01 Jan 1970 00:00:00 GMT';
  for (const name of AUTH_COOKIE_NAMES) {
    document.cookie = `${name}=; Path=/; Domain=${domain}; Expires=${expiredAt}`;
  }
}

/**
 * Rimuove i flag di bootstrap dal sessionStorage. Deve essere chiamata da ogni
 * path di logout/cleanup, altrimenti al rilogin il backend cookie `.AspNet.Cookies`
 * non viene ricreato → satellite 401.
 */
export function clearAuthSessionFlags(): void {
  for (const key of AUTH_SESSION_STORAGE_KEYS) {
    sessionStorage.removeItem(key);
  }
}
